import React, { useCallback, useMemo } from 'react'

import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from '@/components/common/Dialog'
import Spinner from '@/components/common/Spinner'
import {
  COMPANY_FORM_SECTION_CONFIGS,
  getCompanyFields,
} from '@/components/library-v2/companies/form'
import { getValidCompanyPrimaryContactId } from '@/components/library-v2/companies/lib'
import { toNull } from '@/components/library-v2/lib'
import {
  CreateEntityDialogLayout,
  CreateEntityFieldShell,
  type CreateEntityDialogSection,
} from '@/components/library-v2/ui/CreateEntityDialogLayout'
import { FieldInput } from '@/components/library-v2/ui/Merge'
import {
  toEntityDateOnly,
  toEntityIsoDateTime,
} from '@/components/library-v2/ui/Merge/dateField'

import { useCompanies } from '@/hooks/library/useCompanies'
import { useCreateCompany } from '@/hooks/library/useCreateCompany'
import { usePeople } from '@/hooks/library/usePeople'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { CreateEntityCompany, EntityPerson } from '@/types/library/Entity'

interface CreateCompanyDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const defaultValues: CreateEntityCompany = {
  entity_type: 'company',
  name: '',
  legal_name: null,
  dba_name: null,
  aliases: null,
  industry: null,
  description: null,
  relationship_types: null,
  uei: null,
  cage_code: null,
  website_url: null,
  linkedin_url: null,
  parent_entity_company_id: null,
  linked_organization_id: null,
  relationship_status: null,
  owner_user_id: null,
  priority: null,
  last_contacted_at: null,
  company_size: null,
  socioeconomic_categories: null,
  naics_codes: null,
  psc_codes: null,
  vehicles: null,
  sam_registration_status: null,
  sam_expiration_date: null,
  facility_clearance_level: null,
  capabilities_summary: null,
  core_capabilities: null,
  primary_contact_entity_person_id: null,
  tag_ids: [],
}

const sanitizeCompany = (
  data: CreateEntityCompany,
  primaryContactPeople: EntityPerson[],
): CreateEntityCompany => ({
  ...data,
  name: data.name.trim(),
  legal_name: toNull(data.legal_name),
  dba_name: toNull(data.dba_name),
  industry: toNull(data.industry),
  description: toNull(data.description),
  uei: toNull(data.uei),
  cage_code: toNull(data.cage_code),
  website_url: toNull(data.website_url),
  linkedin_url: toNull(data.linkedin_url),
  relationship_status: toNull(data.relationship_status),
  priority: toNull(data.priority),
  last_contacted_at: toEntityIsoDateTime(data.last_contacted_at),
  company_size: toNull(data.company_size),
  sam_registration_status: toNull(data.sam_registration_status),
  sam_expiration_date: toEntityDateOnly(data.sam_expiration_date),
  facility_clearance_level: toNull(data.facility_clearance_level),
  capabilities_summary: toNull(data.capabilities_summary),
  primary_contact_entity_person_id: getValidCompanyPrimaryContactId(
    data.primary_contact_entity_person_id,
    primaryContactPeople,
  ),
  tag_ids: data.tag_ids ?? [],
})

export const CreateCompanyDialog: React.FC<CreateCompanyDialogProps> = ({
  open,
  setOpen,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()
  const { data: companies } = useCompanies({
    orgId: activeOrgId,
    withConfirmed: true,
    withSuggested: false,
  })
  const { data: primaryContactPeople } = usePeople({
    orgId: activeOrgId,
    withConfirmed: true,
    withSuggested: false,
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<CreateEntityCompany>({
    defaultValues,
    mode: 'onChange',
  })

  const { mutateAsync: createCompany, isPending } = useCreateCompany({
    orgId: activeOrgId,
  })
  const handleCreate = useCallback(
    async (data: CreateEntityCompany) => {
      try {
        await createCompany(sanitizeCompany(data, primaryContactPeople ?? []), {
          onSuccess: (created) => {
            setOpen(false)
            reset(defaultValues)
            toast.success('Company added successfully!', {
              action: {
                label: 'View',
                onClick: () => {
                  safePush(
                    `/orgs/${activeOrgId}/library/companies/${created.id}`,
                  )
                },
              },
            })
          },
        })
      } catch (error) {
        console.error('Failed to add new company:', error)
        // Error is handled by the useCreateCompany hook
      }
    },
    [
      activeOrgId,
      createCompany,
      primaryContactPeople,
      reset,
      safePush,
      setOpen,
    ],
  )

  const handleClose = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (!open) {
        reset(defaultValues)
      }
    },
    [setOpen, reset],
  )

  const sections = useMemo<CreateEntityDialogSection[]>(() => {
    const fieldSections = COMPANY_FORM_SECTION_CONFIGS.map((section) => {
      const fields = getCompanyFields(section.fieldKeys)

      return {
        id: section.id,
        label: section.label,
        description: section.description,
        icon: section.icon,
        required: section.required,
        children: (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {fields.map((field) => (
              <CreateEntityFieldShell
                key={field.key}
                label={field.label}
                required={field.required}
                fullWidth={field.type === 'textarea'}
              >
                <FieldInput
                  field={field}
                  control={control}
                  companies={companies}
                  people={
                    field.key === 'primary_contact_entity_person_id'
                      ? primaryContactPeople
                      : undefined
                  }
                />
              </CreateEntityFieldShell>
            ))}
          </div>
        ),
      }
    })

    return fieldSections
  }, [companies, control, primaryContactPeople])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        aria-describedby={undefined}
        className="h-[90vh] max-h-[760px] w-full max-w-[1040px] flex flex-col p-0 overflow-hidden"
        overlayClassName="p-0"
      >
        <form
          onSubmit={handleSubmit(handleCreate)}
          className="flex flex-col flex-1 min-h-0"
        >
          <CreateEntityDialogLayout
            navLabel="Sections"
            title="Create Company"
            description="Capture account details, identifiers, and federal market context."
            sections={sections}
            footer={
              <DialogFooter className="flex justify-end p-5 border-t border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-900">
                <DialogClose asChild>
                  <Button
                    variant="outline-compact"
                    disabled={isPending}
                    type="button"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  variant="primary-compact"
                  type="submit"
                  disabled={isPending || !isValid}
                >
                  {isPending ? (
                    <>
                      <Spinner variant="primary-compact" className="w-4 h-4" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    'Create Company'
                  )}
                </Button>
              </DialogFooter>
            }
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
